import ImageLogo from "@/assets/images/logo.png";
import { FullSingleQuote } from "@/types";
import { formatDatePretty } from "@/utils/utils.util";
import { Document, Image, Page, Text, View } from "@react-pdf/renderer";
import React from "react";
import { createTw } from "react-pdf-tailwind";

type CreateQuotePDFProps = {
  quote: FullSingleQuote;
};

const tw = createTw({
  theme: {
    fontFamily: {
      //   sans: ["Comic Sans"],
    },
    fontSize: {
      base: 8,
    },
    extend: {
      colors: {
        primary: "#65d2d3",
        secondary: "#343961",
      },
    },
  },
});

const CreateQuotePDF: React.FC<CreateQuotePDFProps> = ({ quote }) => {
  const formatCurrency = (value: string | number) => {
    const num = typeof value === "string" ? Number.parseFloat(value) : value;
    return isNaN(num) ? "R 0.00" : `R${num.toFixed(2)}`;
  };

  // Helper function to break long text
  const breakLongText = (text: string, maxLength: number = 15) => {
    if (!text || text.length <= maxLength) return text;
    // Insert spaces or line breaks for long text
    // Using a RegExp constructor to allow dynamic maxLength value
    return text.replace(new RegExp(`(.{${maxLength}})`, "g"), "$1 ");
  };

  const breakLongWords = (text: string, maxLength: number = 15) => {
    if (!text || text.length <= maxLength) return text;
    // breakup by word and check if word exceeds maxLength, using breakLongText
    return text
      .split(" ")
      .map((word) => breakLongText(word, maxLength))
      .join(" ");
  };

  // Component for table header - reusable across pages
  const TableHeader = () => (
    <View
      style={tw(
        "flex flex-row bg-secondary border-black border-b text-white items-center"
      )}
    >
      {/* <View style={tw("flex-[0.6] p-1 text-center items-center")}></View> */}
      <View style={tw("flex-[0.6] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Qty</Text>
      </View>
      <View style={tw("flex-[0.8] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Part Name</Text>
      </View>
      <View style={tw("flex-[1.2] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>{`Drawing\nNumber`}</Text>
      </View>
      <View style={tw("flex-[0.6] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Length</Text>
      </View>
      <View style={tw("flex-[0.6] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Width</Text>
      </View>
      <View style={tw("flex-[0.6] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Thick</Text>
      </View>
      <View style={tw("flex-[1.5] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Material</Text>
      </View>
      <View style={tw("flex-[0.8] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Unit Price</Text>
      </View>
      <View style={tw("flex-[0.8] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Total Price</Text>
      </View>
      {/* <View style={tw("flex-[0.8] p-1 text-left items-center")}>
        <Text style={tw("font-bold text-[8px]")}>Process</Text>
      </View> */}
    </View>
  );

  // Component for individual table row
  const TableRow = ({ item }: { item: (typeof quote.quote_items)[0] }) => (
    <View style={tw("flex flex-row min-h-[20px]")}>
      {/* <View style={tw("flex-[0.6] p-1 text-center flex justify-center")}>
        {item.item_file_meta ? (
          <Image
            src={item.item_file_meta}
            style={tw("h-[20px] w-[20px] m-auto mb-1")}
          />
        ) : null}
      </View> */}
      <View style={tw("flex-[0.6] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>{item.qty} - off</Text>
      </View>
      <View style={tw("flex-[0.8] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>{breakLongWords(item.name, 9)}</Text>
      </View>
      <View style={tw("flex-[1.2] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>
          {breakLongWords(item.item_file_drawing_no || "N/A")}
        </Text>
      </View>
      <View style={tw("flex-[0.6] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>{item.length}</Text>
      </View>
      <View style={tw("flex-[0.6] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>{item.width}</Text>
      </View>
      <View style={tw("flex-[0.6] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>{item.thickness}</Text>
      </View>
      <View style={tw("flex-[1.5] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>
          {breakLongWords(item.material_type)}
        </Text>
      </View>
      <View style={tw("flex-[0.8] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>
          {formatCurrency(item.price_per_item)}
        </Text>
      </View>
      <View style={tw("flex-[0.8] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>
          {formatCurrency(item.total_price_per_item)}
        </Text>
      </View>
      {/* <View style={tw("flex-[0.8] p-1 text-center flex justify-center")}>
        <Text style={tw("text-[8px]")}>
          {breakLongWords(item.process_type, 8)}
        </Text>
      </View> */}
    </View>
  );

  // Calculate how many items can fit per page after the header content
  const itemsPerPage = 10; // Approximate number of items that fit on first page
  const continuationItemsPerPage = 23; // More items can fit on continuation pages

  // Split items into chunks for pagination
  const getPageChunks = () => {
    const chunks = [];
    const items = quote.quote_items;

    if (items.length <= itemsPerPage) {
      // All items fit on first page
      chunks.push(items);
    } else {
      // First page chunk
      chunks.push(items.slice(0, itemsPerPage));

      // Remaining items for continuation pages
      let remainingItems = items.slice(itemsPerPage);
      while (remainingItems.length > 0) {
        chunks.push(remainingItems.slice(0, continuationItemsPerPage));
        remainingItems = remainingItems.slice(continuationItemsPerPage);
      }
    }

    return chunks;
  };

  const pageChunks = getPageChunks();

  return (
    <Document>
      {pageChunks.map((pageItems, pageIndex) => (
        <Page key={pageIndex} size="A4" style={tw("p-10 text-base")}>
          {pageIndex === 0 ? (
            // First page with full header
            <View
              style={tw(
                "border-2 border-b-0 border-black border-solid w-full flex flex-col"
              )}
            >
              {/* Header Section */}
              <View
                style={tw(
                  "flex flex-row items-center justify-between gap-2 p-2"
                )}
              >
                <View style={tw("w-1/5 text-center")}>
                  <Image
                    src={ImageLogo}
                    style={tw("h-[50px] w-[48px] m-auto mb-4")}
                  />
                  <Text style={tw("font-bold mb-2")}>
                    Reg No. 1997003879/07
                  </Text>
                </View>
                <View style={tw("w-3/5 h-auto text-center")}>
                  <Text style={tw("m-0 p-0")}>
                    VULCAN STEEL MANUFACTURING (Pty) Ltd, t/a:
                  </Text>
                  <Text
                    style={tw(
                      "text-[32px] font-bold m-0 p-0 leading-none text-secondary"
                    )}
                  >
                    VULCAN STEEL
                  </Text>
                  <Text style={tw("mt-2 p-0")}>
                    Specialists in steel plates, sheets and profile cutting
                  </Text>
                </View>
                <View style={tw("w-1/5 text-center")}>
                  <Image
                    src={ImageLogo}
                    style={tw("h-[50px] w-[48px] m-auto mb-4")}
                  />
                  <Text style={tw("font-bold mb-2")}>VAT No. 4630170944</Text>
                </View>
              </View>

              {/* Address and Quotation Section */}
              <View style={tw("flex flex-row w-full")}>
                <View style={tw("border-b border-t border-black w-[70%]")}>
                  <View style={tw("flex flex-row border-b border-black")}>
                    <View style={tw("w-1/3 p-2 border-r border-black")}>
                      <Text style={tw("font-bold mb-2")}>
                        Physical Address:
                      </Text>
                      <Text>No. 6-8 Longclaw Drive</Text>
                      <Text>Montague Gardens</Text>
                      <Text>7441</Text>
                    </View>
                    <View style={tw("w-1/3 p-2 border-r border-black")}>
                      <Text style={tw("font-bold mb-2")}>Postal Address:</Text>
                      <Text>P.O. Box 36731</Text>
                      <Text>Chempet</Text>
                      <Text>7442</Text>
                    </View>
                    <View style={tw("w-1/3 p-2")}>
                      <Text style={tw("font-bold mb-2")}>Contact Details:</Text>
                      <Text>Tel: (021) 528 8900</Text>
                      <Text>Fax: 0866 22 78 78</Text>
                      <Text>E-mail: info@vulcansteel.co.za</Text>
                    </View>
                  </View>
                  <View style={tw("p-2 border-b border-black")}>
                    <Text style={tw("font-bold text-[10px] pb-2")}>
                      Customer: {quote.company_name}
                    </Text>
                    <View style={tw("flex flex-row justify-between")}>
                      <View style={tw("pl-2")}>
                        <Text style={tw("pb-0")}>{quote.branch_address1}</Text>
                        <Text style={tw("pb-0")}>{quote.branch_address2}</Text>
                        <Text style={tw("pb-0")}>{quote.branch_suburb}</Text>
                        <Text style={tw("pb-0")}>{quote.branch_city}</Text>
                        <Text style={tw("pb-0")}>{quote.branch_province}</Text>
                        <Text style={tw("pb-1")}>{quote.branch_pin_code}</Text>
                        <Text>Phone: {quote.company_phone_number || ""}</Text>
                        {/* <Text>Fax: {customerData.contact.fax}</Text> */}
                        <Text>Email: {quote.quoted_by_email}</Text>
                      </View>
                      <View>
                        <Text style={tw("font-bold text-[10px] pb-2")}>
                          Contact: {quote.quoted_by_name}
                        </Text>
                        <View style={tw("pl-2")}>
                          <Text>Phone: {quote.quoted_by_phone_number}</Text>
                          {/* <Text>Fax: {customerData.contact.fax}</Text> */}
                          <Text>Email: {quote.quoted_by_email || "N/A"}</Text>
                        </View>
                      </View>
                    </View>
                  </View>
                  <View style={tw("flex flex-row")}>
                    <View style={tw("w-full flex flex-row p-2 gap-2")}>
                      <Text style={tw("font-bold")}>For Attention:</Text>
                      <Text>{quote.user_name}</Text>
                    </View>
                    {/* <View style={tw("w-1/2 flex flex-row p-2 gap-2")}>
                      <Text style={tw("font-bold")}>Your Order Number:</Text>
                      <Text style={tw("font-bold")}>{quote.order_number}</Text>
                    </View> */}
                  </View>
                </View>

                <View
                  style={tw(
                    "border border-black w-[30%] border-r-0 flex flex-col justify-between"
                  )}
                >
                  <View>
                    <View style={tw("border-b border-black")}>
                      <Text style={tw("text-[18px] font-bold text-center")}>
                        Quotation
                      </Text>
                    </View>
                    <View style={tw("border-b border-black")}>
                      <Text
                        style={tw(
                          "text-[14px] font-bold text-right p-1 pl-2 text-primary"
                        )}
                      >
                        {quote.quote_number}
                      </Text>
                    </View>
                    <View style={tw("flex flex-row border-b border-black")}>
                      <View
                        style={tw("w-1/2 text-center border-r border-black")}
                      >
                        <Text style={tw("p-1")}>
                          {formatDatePretty(quote.quote_created_at)}
                        </Text>
                      </View>
                      <View style={tw("w-1/2 text-center")}>
                        <Text
                          style={tw("p-1")}
                          render={({ pageNumber, totalPages }) =>
                            `Page ${pageNumber} of ${totalPages}`
                          }
                          fixed
                        />
                      </View>
                    </View>
                    <View style={tw("p-2")}>
                      <Text style={tw("font-bold text-[10px] pb-1")}>
                        Delivery Address
                      </Text>
                      <Text>{quote.branch_address1}</Text>
                      <Text>{quote.branch_address2}</Text>
                      <Text>{quote.branch_suburb}</Text>
                      <Text>{quote.branch_city}</Text>
                      <Text>{quote.branch_province}</Text>
                      <Text>{quote.branch_pin_code}</Text>
                    </View>
                  </View>
                  {/* <View style={tw("p-2 border-t border-black")}>
                    <Text style={tw("font-bold text-center")}>
                      This Job is for Delivery
                    </Text>
                  </View> */}
                </View>
              </View>

              {/* Quote Description */}
              <View style={tw("border-b border-black")}>
                <Text style={tw("font-bold text-[10px] p-2")}>
                  We take pleasure in submitting our quotation for the following
                  components
                </Text>
              </View>

              {/* Quote Details Table */}
              <View
                style={tw(
                  "border-b border-black flex flex-row w-full text-center"
                )}
              >
                <View style={tw("flex flex-col flex-[1.2]")}>
                  <View style={tw("p-2 border-b border-black")}>
                    <Text style={tw("font-bold text-[10px]")}>Account</Text>
                  </View>
                  <View style={tw("p-2")}>
                    <Text style={tw("text-[10px]")}>{quote.branch_id}</Text>
                  </View>
                </View>
                <View
                  style={tw("border-l border-black flex flex-col flex-[1]")}
                >
                  <View style={tw("p-2 border-b border-black")}>
                    <Text style={tw("font-bold text-[10px]")}>Expiry</Text>
                  </View>
                  <View style={tw("p-2")}>
                    <Text style={tw("text-[10px]")}>
                      {formatDatePretty(quote.quote_expiry_at)}
                    </Text>
                  </View>
                </View>
                <View
                  style={tw("border-l border-black flex flex-col flex-[1.1]")}
                >
                  <View style={tw("p-2 border-b border-black")}>
                    <Text style={tw("font-bold text-[10px]")}>
                      VDS Job Number
                    </Text>
                  </View>
                  <View style={tw("p-2")}>
                    <Text style={tw("text-[10px]")}>
                      {quote.vds_job_number}
                    </Text>
                  </View>
                </View>
                <View
                  style={tw("border-l border-black flex flex-col flex-[1.3]")}
                >
                  <View style={tw("p-2 border-b border-black")}>
                    <Text style={tw("font-bold text-[10px]")}>
                      Customer Reference
                    </Text>
                  </View>
                  <View style={tw("p-2")}>
                    <Text style={tw("text-[10px]")}>
                      {quote.customer_reference || ""}
                    </Text>
                  </View>
                </View>
                <View
                  style={tw("border-l border-black flex flex-col flex-[1.2]")}
                >
                  <View style={tw("p-2 border-b border-black")}>
                    <Text style={tw("font-bold text-[10px]")}>Quoted By</Text>
                  </View>
                  <View style={tw("p-2")}>
                    <Text style={tw("text-[10px]")}>
                      {quote.quoted_by_name || "N/A"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          ) : (
            // Continuation pages with minimal header
            <View
              style={tw(
                "border-2 border-black border-solid w-full flex flex-col border-b-0"
              )}
            >
              <View
                style={tw(
                  "flex flex-row items-center justify-between p-2 border-b border-black"
                )}
              >
                <View style={tw("flex flex-row items-center gap-4")}>
                  <Image src={ImageLogo} style={tw("h-[30px] w-[28px]")} />
                  <Text style={tw("text-[14px] font-bold")}>
                    VULCAN STEEL - Quotation {quote.quote_number}
                  </Text>
                </View>
                <Text
                  style={tw("text-[12px]")}
                  render={({ pageNumber, totalPages }) =>
                    `Page ${pageNumber} of ${totalPages}`
                  }
                  fixed
                />
              </View>
            </View>
          )}

          {/* Quote Items Table - appears on every page */}
          <View
            style={tw(
              "border-l border-r border-b border-black border-2 border-t-0"
            )}
          >
            <TableHeader />
            {pageItems.map((item) => (
              <TableRow key={item.id} item={item} />
            ))}
          </View>

          {/* Add totals and terms only on the last page */}
          {pageIndex === pageChunks.length - 1 && (
            <View style={tw("mt-4")}>
              {/* Totals Section */}
              <View style={tw("flex flex-row justify-between")}>
                {/* Terms and Notes - Left Side */}
                <View style={tw("w-[60%] pr-4")}>
                  <Text style={tw("font-bold text-[10px] mb-2")}>
                    Please Note:
                  </Text>
                  <Text style={tw("text-[8px] mb-4 leading-relaxed")}>
                    This quotation is subject to our standard terms and
                    conditions as stated on our opening account forms. For a
                    copy, please enquire with the sales team. All prices quoted
                    are subject to steel mill and transport cost fluctuations.
                    Sign and return to us by fax, e-mail or hand with your order
                    number in the space provided for prompt processing of this
                    quotation. It remains the responsibility of the customer to
                    verify quantities and material grades and thicknesses on
                    this quotation before placing the order.
                  </Text>

                  {/* Signature Section */}
                  <View style={tw("flex flex-row justify-between mt-6")}>
                    <View style={tw("w-[30%]")}>
                      <Text style={tw("text-[10px] mb-1")}>Received by</Text>
                      <View style={tw("border-b border-black h-[20px]")}></View>
                    </View>
                    <View style={tw("w-[30%]")}>
                      <Text style={tw("text-[10px] mb-1")}>Date</Text>
                      <View style={tw("border-b border-black h-[20px]")}></View>
                    </View>
                    <View style={tw("w-[30%]")}>
                      <Text style={tw("text-[10px] mb-1")}>Signature</Text>
                      <View style={tw("border-b border-black h-[20px]")}></View>
                    </View>
                  </View>

                  {/* Additional Terms */}
                  <View style={tw("mt-6")}>
                    <Text style={tw("font-bold text-[10px] mb-2")}>
                      Additional Terms:
                    </Text>
                    <Text style={tw("font-bold text-[9px] mb-1")}>
                      BENDING TOLERANCE:
                    </Text>
                    <Text style={tw("text-[8px] pb-[2px]")}>
                      Thickness 0.5mm - 4mm: +/-0.8mm/1.5mm
                    </Text>
                    <Text style={tw("text-[8px] pb-[2px]")}>
                      Thickness 5mm - 10mm +/-1.5mm/2mm
                    </Text>
                    <Text style={tw("text-[8px] pb-[2px]")}>
                      Thickness 12mm - 20mm +/-2mm/3mm
                    </Text>
                    <Text style={tw("text-[8px]")}>
                      Thickness 25mm - 40mm +/-3mm/5mm
                    </Text>
                  </View>
                </View>

                {/* Totals Table - Right Side */}
                <View style={tw("w-[40%]")}>
                  <View style={tw("border border-black")}>
                    <View style={tw("flex flex-row border-b border-black")}>
                      <View style={tw("w-[70%] p-2 border-r border-black")}>
                        <Text style={tw("font-bold text-[10px]")}>
                          Total (Excl. VAT)
                        </Text>
                      </View>
                      <View style={tw("w-[30%] p-2 text-right")}>
                        <Text style={tw("font-bold text-[10px]")}>
                          {formatCurrency(quote.total_excl_vat)}
                        </Text>
                      </View>
                    </View>
                    <View style={tw("flex flex-row border-b border-black")}>
                      <View style={tw("w-[70%] p-2 border-r border-black")}>
                        <Text style={tw("font-bold text-[10px]")}>VAT</Text>
                      </View>
                      <View style={tw("w-[30%] p-2 text-right")}>
                        <Text style={tw("font-bold text-[10px]")}>
                          {formatCurrency(quote.vat_amount)}
                        </Text>
                      </View>
                    </View>
                    <View style={tw("flex flex-row")}>
                      <View style={tw("w-[70%] p-2 border-r border-black")}>
                        <Text style={tw("font-bold text-[10px]")}>
                          Total (Incl. VAT)
                        </Text>
                      </View>
                      <View style={tw("w-[30%] p-2 text-right")}>
                        <Text style={tw("font-bold text-[10px]")}>
                          {formatCurrency(quote.total_incl_vat)}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}
        </Page>
      ))}
    </Document>
  );
};

export default CreateQuotePDF;
